/**
 * useEditingClient — guarded state hook for the "client we're editing"
 * flag in dialog/wizard contexts.
 *
 * Why this exists
 * ───────────────
 * Extracted dialogs (IntakeWizardDialog, AssignProvidersDialog, etc.)
 * bubble values up through `setSelectedClient(...)` callbacks.  Those
 * values can be one of:
 *   - a real persisted client doc (✅ safe to treat as edit-existing)
 *   - a stub fetched via a draft id (❌ would flip dialog into "Update"
 *     mode for a not-yet-finalized client)
 *   - the `{ id: 'new', ... }` sentinel synthesized for the new-client
 *     flow (❌ submit would PUT /clients/new → 404 "Client not found")
 *   - null / undefined / partial garbage from race conditions
 *
 * Blindly piping `setSelectedClient → setEditingClient` was the root
 * cause of the March 2026 P0 intake-wizard bug ("selecting provider
 * wiped form, said 'Update Client', submit returned 'client not
 * found'").  This hook makes that class of regression structurally
 * impossible: `safeSetEditingClient` REFUSES any payload that isn't a
 * real-client refresh on the client we were already editing.
 *
 * API
 * ───
 *   const [editingClient, setEditingClient, safeSetEditingClient]
 *     = useEditingClient(initial);
 *
 *   - editingClient          — current value (or `initial`).
 *   - setEditingClient(next) — RAW setter.  Use for explicit,
 *                              known-safe transitions (e.g., `open()`
 *                              loading a real client from the API,
 *                              or `close()` clearing to null).
 *   - safeSetEditingClient(next) — gated setter.  Returns `true` if
 *                              the value was accepted, `false` if it
 *                              was refused.  Wire this into any
 *                              child-dialog `setSelectedClient` shim.
 *
 * Gate rules (must ALL be true to accept):
 *   1. `next` is a non-null object with a string `id`.
 *   2. `id !== 'new'`  (the sentinel for new-client flow).
 *   3. There is already an `editingClient` in state.
 *   4. `next.id === editingClient.id`  (it's a refresh, not a swap).
 */
import { useState, useCallback, useRef, useEffect } from 'react';

const isRealClientId = (id) => (
  typeof id === 'string' && id.length > 0 && id !== 'new'
);

export const useEditingClient = (initial = null) => {
  const [editingClient, setEditingClientRaw] = useState(initial);

  // Keep a ref mirror of the current value so the callback closure can
  // read the latest state without re-creating on every render (and
  // without forcing callers to memoise the result).
  const editingClientRef = useRef(editingClient);
  useEffect(() => { editingClientRef.current = editingClient; }, [editingClient]);

  const safeSetEditingClient = useCallback((next) => {
    if (!next || typeof next !== 'object') return false;
    if (!isRealClientId(next.id)) return false;
    const current = editingClientRef.current;
    if (!current || current.id !== next.id) return false;
    setEditingClientRaw(next);
    return true;
  }, []);

  return [editingClient, setEditingClientRaw, safeSetEditingClient];
};

export default useEditingClient;
